import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IconoirCheckCircle } from '@ey-xd/motif-icon';
import {
  MotifButtonModule,
  MotifIconModule,
  MotifProgressBarModule,
} from '@ey-xd/ng-motif';
import { CustomMotifDownloaderComponent } from 'app/core/components/custom-motif-downloader/custom-motif-downloader.component';
import { CustomMotifUploaderComponent } from 'app/core/components/custom-motif-uploader/custom-motif-uploader.component';
import { ApiResponseReader } from 'app/core/service/api/api-response.service';
import { ChartOfAccountService } from 'app/core/service/chart-of-account/chart-of-account.service';
import { FileManipulationService } from 'app/core/service/file-manipulation/file-manipulation.service';
import { TbeService } from 'app/core/service/tbe/tbe.service';
import { ToastService } from 'app/core/service/toast/toast.service';
import { ApiFile, ApiItem, ApiJob } from 'app/models/api-response';
import { CoaSummary } from 'app/models/coa-summary';
import { ProcessStatus } from 'app/models/process-status';
import { ProgressStep } from 'app/models/progress-steps';
import { take } from 'rxjs';

@Component({
  selector: 'app-coa-agent',
  standalone: true,
  imports: [
    CommonModule,
    CustomMotifUploaderComponent,
    CustomMotifDownloaderComponent,
    MotifProgressBarModule,
    MotifIconModule,
    MotifButtonModule,
  ],
  templateUrl: './coa-agent.component.html',
  styleUrl: './coa-agent.component.scss',
})
export class CoaAgentComponent {
  IconoirCheckCircle = IconoirCheckCircle;

  @Input() currentStep: number = 0;
  @Input() steps: ProgressStep[] = [];
  @Input() trialBalanceReportId: string = '';

  // Agent 2 Chart of account dropdown data variables
  @Input() selectedCoAOption?: ApiItem = undefined;

  // to control Agent 2 spinners
  // step one
  runCoaMappingLoading: boolean = false;

  coaMappingCanceled: boolean = false;
  // step two
  runCoAFinalMapLoading: boolean = false;
  coaFinalMapCanceled: boolean = false;
  //

  // Agent 2 File uploading Steps variables to store file and control errors
  @Input() coaMappingFile: File | any = undefined;
  coaMappingFileHasError: boolean | any = false;
  @Input() savedCoaMappingFile: ApiFile | undefined = undefined;

  @Input() coaFinalMappingFile: File | any = undefined;
  coaFinalMappingFileFileHasError: boolean | any = false;
  savedCoaFinalMappingFile: ApiFile | undefined = undefined;

  // This is to store the data from Agent 2 level 1 file that we will download
  // on the second step of Chart of account
  @Input() coaMappedFileToDownload: any = undefined;
  @Output() coaMappedFileToDownloadEmitter: EventEmitter<any> =
    new EventEmitter<any>();

  // This is to store the data from Agent 2 final generated file that we will download
  // on the forth step of Chart of account
  @Input() coaFinalMappedFileToDownload: any = undefined;

  // Step eight summary data
  @Input() coaSummary?: CoaSummary | any;
  @Output() coaSummaryEmitter = new EventEmitter<CoaSummary | any>();

  // process duration messages for agent 2
  @Input() stepFiveDuration: string = '';
  @Input() stepSevenDuration: string = '';

  @Output() goToNextStepEmitter = new EventEmitter<boolean>();
  @Output() goToPreviousStepEmitter = new EventEmitter<boolean>();

  // data to undo the last step of agent 2
  toUndoStep5?: { tb: string; index: string };
  @Input() toUndoStep4?: { tb: string };

  @Output() goBackToAgentOneAfterUndoEmitter = new EventEmitter<any>();

  constructor(
    private toastService: ToastService,
    private coaService: ChartOfAccountService,
    private tbeService: TbeService,
    private fileManipulationService: FileManipulationService
  ) {}

  ngOnInit(): void {
    this.toastService.clearToastWithTag(true);
  }

  // Agent 2 step one run function
  runLatestMapping() {
    if (!this.selectedCoAOption) {
      this.toastService.showToast(
        'error',
        'Please select a Chart of Account Mapping to continue.',
        {
          classTheme: '',
          autoHide: false,
        }
      );
      return;
    }

    const fileToSend: File | null = this.coaMappingFile || null;

    this.runCoaMappingLoading = true;

    const tb = this.trialBalanceReportId;
    const index = this.selectedCoAOption.id!;
    this.toUndoStep5 = { tb, index };

    this.coaService
      .postCoaStart(tb, fileToSend, index)
      .pipe(take(1))
      .subscribe({
        next: (data: any) => {
          this.getDataForCoAStepTwo(this.trialBalanceReportId);
        },
        error: (error: HttpErrorResponse) => {
          this.runCoaMappingLoading = false;
          this.toastService.showHttpResponseErrorMessage(error);
        },
      });
  }

  cancelLatestMappingRun() {
    // mock cancel
    this.coaMappingFile = undefined;
    this.runCoaMappingLoading = false;
    this.coaMappingCanceled = true;
  }

  goBackToAgentOne(): void {
    if (this.toUndoStep4) {
      this.tbeService
        .undoCompleteTrialBalance(this.toUndoStep4!.tb)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.goBackToAgentOneAfterUndo();
          },
          error: (error: HttpErrorResponse) => {
            this.toastService.showHttpResponseErrorMessage(
              error,
              'Could not undo the last step.'
            );
          },
        });
    } else {
      this.goBackToAgentOneAfterUndo();
    }
  }

  goBackToAgentOneAfterUndo(): void {
    this.goBackToAgentOneAfterUndoEmitter.emit();
  }

  /**
   * Go back from step 6 to step 5.
   */
  goToPreviousStep_5(): void {
    if (this.toUndoStep5) {
      this.coaService
        .undoPostCoaStart(this.toUndoStep5!.tb, this.toUndoStep5!.index)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.goToPreviousStep_5_afterUndo();
          },
          error: (error: HttpErrorResponse) => {
            this.toastService.showHttpResponseErrorMessage(
              error,
              'Could not undo the last step.'
            );
          },
        });
    } else {
      this.goToPreviousStep_5_afterUndo();
    }
  }

  private goToPreviousStep_5_afterUndo(): void {
    this.toUndoStep5 = undefined;

    this.goToPreviousStep(true);
  }

  getDataForCoAStepTwo(coaProjectId: string) {
    if (this.coaMappingCanceled) {
      this.coaMappingCanceled = false;
      return;
    }

    this.coaService
      .getCoaProject(coaProjectId)
      .pipe(take(1))
      .subscribe({
        next: (data: ApiResponseReader) => {
          const status: ProcessStatus = data.status;
          const files: ApiFile[] = data.files;
          const jobs: ApiJob[] = data.jobs;

          if (
            status === ProcessStatus.Completed ||
            status === ProcessStatus.WaitingForUser
          ) {
            // 30 is success
            // 25 is waiting for user
            this.runCoaMappingLoading = false;

            if (this.coaMappingFile) {
              // 14 is the one I upload in the step 1
              // 15 refers to the cleansed TB File
              this.savedCoaMappingFile =
                this.fileManipulationService.getCleansedFile(files, false, 14);
            } else {
              this.savedCoaMappingFile = undefined;
            }

            this.coaMappedFileToDownload =
              this.fileManipulationService.getCleansedFile(files, false, 11);

            // Put a fixed filename to avoid using the generated one with the id
            this.coaMappedFileToDownload.filename =
              'AI Generated Income Statement / Balance Sheet Classification.xlsx';

            this.coaMappedFileToDownloadEmitter.emit(
              this.coaMappedFileToDownload
            );

            const duration: number | undefined = this.getJobByType(
              jobs,
              20
            )?.time_ms;
            // TODO: Put real duration in seconds in the following message
            this.stepFiveDuration = `Level 1 Account Mapping completed in ${
              duration! / 1000
            } sec.`;
            this.goToNextStep(); // go to step 6 (step 2 of agent 2)
          } else if (
            status === ProcessStatus.Created ||
            status === ProcessStatus.Running
          ) {
            // 10 & 20 is processing
            setTimeout(() => {
              this.getDataForCoAStepTwo(coaProjectId);
            }, 15000);
          } else {
            //40 or else are failures
            this.runCoaMappingLoading = false;

            if (this.coaMappingCanceled) {
              this.coaMappingCanceled = false;
            } else {
              //TODO: Find a message that fits the process error better
              this.toastService.showToast(
                'error',
                `Chart of Account Mapping failed. Run the process again. If the error persist, please contact servicedesk@ey.com`,
                {
                  classTheme: '',
                  autoHide: false,
                }
              );
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this.runCoaMappingLoading = false;

          if (this.coaMappingCanceled) {
            this.coaMappingCanceled = false;
          } else {
            this.toastService.showHttpResponseErrorMessage(error);
          }
        },
      });
  }

  //Agent 2 step three run function
  runValidatedMapping() {
    const fileToSend: File | null = this.coaFinalMappingFile || null;

    this.runCoAFinalMapLoading = true;

    this.coaService
      .putLevel1Project(this.trialBalanceReportId, fileToSend)
      .pipe(take(1))
      .subscribe({
        next: (data: any) => {
          this.getDataForCoAStepFour(this.trialBalanceReportId);
        },
        error: (error: HttpErrorResponse) => {
          this.runCoAFinalMapLoading = false;
          this.toastService.showHttpResponseErrorMessage(error);
        },
      });
  }

  cancelValidatedMappingRun() {
    // mock cancel
    this.coaFinalMappingFile = undefined;
    this.runCoAFinalMapLoading = false;
    this.coaFinalMapCanceled = true;
  }

  getDataForCoAStepFour(coaProjectId: string) {
    if (this.coaFinalMapCanceled) {
      this.coaFinalMapCanceled = false;
      return;
    }

    if (this.coaSummary) {
      this.coaSummary = undefined;
    }

    this.coaService
      .getCoaProject(coaProjectId)
      .pipe(take(1))
      .subscribe({
        next: (data: ApiResponseReader) => {
          const status: number = data.status;
          const files: ApiFile[] = data.files;
          const jobs: ApiJob[] = data.jobs;

          if (status === ProcessStatus.Completed) {
            // 30 is success
            this.runCoAFinalMapLoading = false;
            this.coaFinalMappedFileToDownload =
              this.fileManipulationService.getCleansedFile(files, false, 13);

            this.coaFinalMappedFileToDownload.filename =
              'AI Generated Final Chart of Account Mapping.xlsx';

            if (this.coaFinalMappingFile) {
              // 12 is the one I upload in the step 3
              this.savedCoaFinalMappingFile =
                this.fileManipulationService.getCleansedFile(files, false, 12);
            }

            this.coaSummary = data.coaSummary;
            this.coaSummaryEmitter.emit(this.coaSummary);

            const duration: number | undefined = this.getJobByType(
              jobs,
              21
            )?.time_ms;
            this.stepSevenDuration = `Final Account Mapping completed in ${
              duration! / 1000
            } sec.`;
            this.goToNextStep(); // go to step 8 (step 4 of agent 2)
          } else if (
            status === ProcessStatus.Created ||
            status === ProcessStatus.Running
          ) {
            // 10 & 20 is processing
            setTimeout(() => {
              this.getDataForCoAStepFour(coaProjectId);
            }, 15000);
          } else {
            //40 or else are failures
            this.runCoAFinalMapLoading = false;
            if (this.coaFinalMapCanceled) {
              this.coaFinalMapCanceled = false;
            } else {
              this.toastService.showToast(
                'error',
                `Chart of Account Validation failed. Run the process again. If the error persist, please contact servicedesk@ey.com`
              );
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this.runCoAFinalMapLoading = false;

          if (this.coaFinalMapCanceled) {
            this.coaFinalMapCanceled = false;
          } else {
            this.toastService.showHttpResponseErrorMessage(error);
          }
        },
      });
  }

  //next steps emitter control functions
  goToNextStep(clearToast: boolean = false) {
    if (clearToast) {
      this.goToNextStepEmitter.emit(true);
      return;
    }
    this.goToNextStepEmitter.emit(false);
  }

  goToPreviousStep(clearToast: boolean = false) {
    if (clearToast) {
      this.goToPreviousStepEmitter.emit(true);
      return;
    }
    this.goToPreviousStepEmitter.emit(false);
  }
  //

  // Handle file funtions selection for Agent 2 file uploaders
  onCoaMappingFileUpload(event: Event): void {
    this.coaMappingFile = event;
  }

  onCoaMappingFileHasErrorHasError(event: boolean): void {
    this.coaMappingFileHasError = event;
  }

  onCoaFinalMappingFileUpload(event: Event): void {
    this.coaFinalMappingFile = event;
  }

  onCoaFinalMappingFileFileHasError(event: boolean): void {
    this.coaFinalMappingFileFileHasError = event;
  }
  /**
   * END OF Handle file funtions selection for Agent 2 file uploaders
   **/

  /**
   * Private and utility functions
   * */
  private getJobByType(jobs: ApiJob[], type: number) {
    const job = jobs.find((j: ApiJob) => j.type === type);

    if (job) {
      return job;
    }
    return undefined;
  }
}
